<script setup lang="ts">
/**
 * Survey answer component for multiple choice questions.
 * It allows users to select multiple answers from a list of options.
 * Selected answers are highlighted, and a "Validate" button appears
 */

import SurveyAnswerButton from "@/survey/components/survey-modal/answers/SurveyAnswerButton.vue";
import {MultipleChoiceSurveyQuestion, type SurveyQuestionAnswer} from "@/core/interfaces/survey/Survey";
import {ref} from "vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {SurveyAnswerComponentEmit, SurveyAnswerComponentProps} from "@/survey/components/survey-modal/answers/index";

const {question} = defineProps<SurveyAnswerComponentProps<MultipleChoiceSurveyQuestion>>();
const emits = defineEmits<SurveyAnswerComponentEmit<MultipleChoiceSurveyQuestion>>();

const localQuestion = ref(question);

/**
 * Tell if this question was previously answered by the user.
 *
 * @param {SurveyQuestionAnswer} answer
 * @return {boolean}
 */
const previouslyAnswered = (answer: SurveyQuestionAnswer): boolean => {
    if (localQuestion.value.user_answer) {
        return !!localQuestion.value.user_answer.value.find(item => {
            return item.toString() === answer.value.toString();
        });
    }

    return false;
};

/**
 * On answer press.
 * @param answer
 */
const onAnswerPress = (answer: SurveyQuestionAnswer) => {
    const answerAsString = answer.value.toString();
    if (localQuestion.value.user_answer === null) {
        localQuestion.value.user_answer = {value: [answerAsString]};
    } else {
        const answerIndex = localQuestion.value.user_answer.value.findIndex(
            previousAnswer => previousAnswer === answer.value.toString()
        );
        if (answerIndex === -1) {
            localQuestion.value.user_answer.value.push(answerAsString);
        } else {
            localQuestion.value.user_answer.value.splice(answerIndex, 1);
        }
    }
};

</script>

<template>
  <div>
    <div class="flex flex-wrap flex-row gap-2 justify-center">
      <survey-answer-button
        v-for="answer in localQuestion.answers"
        :key="answer.value.toString()"
        :style="{'background-color': answer?.color}"
        :class="[{'bg-primary': previouslyAnswered(answer)}]"
        @click="onAnswerPress(answer)"
      >
        <span
          class="font-content"
          :style="{'color': answer?.color ? 'white' : 'black'}"
          :class="[{'text-white': previouslyAnswered(answer)}]"
        >
          {{ answer.label }}
        </span>
      </survey-answer-button>
    </div>
    <div v-if="localQuestion.user_answer?.value.length" class="flex justify-end mt-2">
      <BaseButton
        primary
        @click="emits('onPress', localQuestion.user_answer)"
      >
        <span class="font-content">Valider</span>
      </BaseButton>
    </div>
  </div>
</template>
